import { useState } from "react";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { LEAD_STATUSES } from "@/lib/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ReasonDialog } from "@/components/ReasonDialog";

const LOST_STATUSES = ["Lead Lost"];
const WON_STATUS = "Qualified";

export function LeadStatusSelect({ leadId, value, lostReason, lostComments, wonReason, wonComments, onChanged, className = "", size = "sm" }) {
  const [pending, setPending] = useState(null); // { status, kind }
  const [saving, setSaving] = useState(false);

  const apply = async (status, extra = {}) => {
    setSaving(true);
    try {
      await api.put(`/leads/${leadId}`, { lead_status: status, ...extra });
      toast.success(`Status changed to ${status}`);
      setPending(null);
      onChanged?.(status);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const onSelect = (status) => {
    if (status === value) return;
    if (LOST_STATUSES.includes(status)) setPending({ status, kind: "lost" });
    else if (status === WON_STATUS) setPending({ status, kind: "won" });
    else apply(status);
  };

  const editKind = LOST_STATUSES.includes(value) ? "lost" : value === WON_STATUS ? "won" : null;
  const h = size === "xs" ? "h-8 text-xs" : "h-9";

  return (
    <>
      <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <Select value={value} onValueChange={onSelect}>
        <SelectTrigger className={`${h} ${className}`} data-testid={`lead-status-select-${leadId}`}><SelectValue /></SelectTrigger>
        <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
      </Select>
      {editKind && (
        <button type="button" onClick={() => setPending({ status: value, kind: editKind })}
          title={`Edit ${editKind} reason`}
          className="h-7 w-7 inline-flex items-center justify-center rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 shrink-0 align-middle"
          data-testid={`lead-reason-edit-${leadId}`}>
          <Pencil className="h-3.5 w-3.5" />
        </button>
      )}
      </span>
      <ReasonDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)} kind={pending?.kind || "lost"}
        title={pending?.kind === "won" ? `Lead won — “${pending?.status}”` : `Mark lead as “${pending?.status}”`}
        initialReason={(pending?.kind === "won" ? wonReason : lostReason) || ""}
        initialComments={(pending?.kind === "won" ? wonComments : lostComments) || ""}
        saving={saving}
        onConfirm={(reason, comments) => apply(pending.status, pending.kind === "won"
          ? { won_reason: reason, won_comments: comments }
          : { lost_reason: reason, lost_comments: comments })} />
    </>
  );
}
